// Skills & MCP — the capability registry, as cards.
//
// V2PORT-WS3 (Founder-approved 2026-08-28). Reads the registry that already
// exists: `GET /api/brain/capabilities`, founder-only and flag-gated behind
// `CIPHER_CAPABILITY_API_V1`. No endpoint is added and none is needed.
//
// WITH THE FLAG OFF THIS IS NOT AN ERROR
// ======================================
// Every route on that API returns 404 by design when the flag is unset, and
// `useBrainResource` maps 404 to `disabled`. So this surface renders "not
// enabled in this environment" rather than a failure, which is the truth.
//
// BUILT ON WHAT THE API RETURNS, NOT ON THE CATALOGUE FILE
// ========================================================
// The mockup read `capability_library/CATALOG.json` directly and could show
// purpose, departments, risk and environment. The API returns a deliberate
// whitelist that omits three of those, and returns something better in their
// place: four INDEPENDENT dimensions — certification, availability, lifecycle
// and health — which `capability/contract.py` keeps separate on purpose,
// because collapsing them "forces a lie in at least one direction, and the lie
// that matters is the optimistic one".
//
// So this surface renders four dimensions per capability and never reduces them
// to one status word.
//
// LAST USED IS EMPTY, AND THAT IS THE FINDING
// ===========================================
// It comes from the trace spine (`/{id}/usage` -> `last_used_at`), and nothing
// in the production agent runtime has ever recorded a Skill retrieval — the
// recorder exists and is called by nothing. Per `contract.py`, a capability
// nobody has invoked is `instrumented: false` with every counter null, NEVER
// zero, "which would read as measured, and nobody used it". This renders that
// distinction rather than flattening it.
import React, { useMemo, useState } from "react";

import { useBrainResource } from "../../../components/brain/lib/hooks";
import SkillAccess from "./SkillAccess";
import EmptyState from "../../components/EmptyState";
import PageHead, { SectionTitle } from "../../components/PageHead";
import "./skills-mcp.css";

/** The four dimensions, each with its own vocabulary. Never merged. */
export const DIMENSIONS = [
  { key: "certification", label: "CERTIFICATION" },
  { key: "availability", label: "AVAILABILITY" },
  { key: "lifecycle", label: "LIFECYCLE" },
  { key: "health", label: "HEALTH" },
];

const TONE = {
  certified: "ok", available: "ok", active: "ok", healthy: "ok",
  adapted: "info",
  uncertified: "unknown", unknown: "unknown", not_required: "unknown",
  credential_required: "warn", environment_required: "warn",
  degraded: "warn", deprecated: "warn", missing: "warn",
  validation_failed: "crit", unreachable: "crit", retired: "crit",
  satisfied: "ok",
  // The four LIVE probe outcomes. `not_probeable` is deliberately `unknown`,
  // not `warn`: Cipher cannot see a stdio server on someone else's machine,
  // and that is an honest absence of a measurement, not a fault to fix.
  connected: "ok", credential_absent: "warn",
  not_probeable: "unknown",
};
/** Anything unrecognised stays `unknown` rather than reading as healthy. */
export function tone(value) {
  return TONE[String(value == null ? "" : value).toLowerCase()] || "unknown";
}
const words = (v) => String(v == null ? "—" : v).replace(/_/g, " ").toUpperCase();

// KIND, NOT ORIGIN (SKILLS-MCP-CARD-ENRICHMENT, 2026-09-03).
// The badge used to be classed on `origin`, and origin collapses four kinds
// into two — `_ORIGIN_BY_KIND` maps skill and internal_tool both to "internal",
// mcp_server and mcp_tool both to "mcp". So a Skill and an Internal Tool wore
// the same teal and the grid read as one undifferentiated wall. Four kinds now
// carry four colours.
const KIND_CLASS = {
  skill: "skill",
  internal_tool: "tool",
  mcp_server: "mcpserver",
  mcp_tool: "mcptool",
};

// DEPARTMENT COLOURS — eleven fixed slots, assigned by name.
// Keyed off the canonical department list so a department always draws the same
// colour on every card and across reloads; a name outside the list falls to a
// neutral slot rather than colliding with a real one. Which colour maps to
// which department is not meaningful — only that it is stable and distinct.
const DEPARTMENT_SLOT = {
  analytics: 1, campaign_operations: 2, compliance: 3, creative_intelligence: 4,
  creative_production: 5, engineering: 6, maintenance: 7, offer_discovery: 8,
  offer_performance: 9, operator_development: 10, testing: 11,
};
const deptSlot = (d) => DEPARTMENT_SLOT[d] || 0;

const ORIGINS = [
  { id: "all", label: "All" },
  { id: "internal", label: "Skills & tools" },
  { id: "mcp", label: "MCP" },
];

export function matches(cap, { origin, query }) {
  if (origin !== "all" && cap.origin !== origin) return false;
  if (!query) return true;
  const hay = [cap.name, cap.capability_id, cap.description, cap.kind, cap.source]
    .join(" ").toLowerCase();
  return query.split(/\s+/).every((t) => hay.includes(t));
}

function Card({ cap, onOpen }) {
  return (
    <article className="sm-card" tabIndex={0} role="button"
             aria-label={`${cap.name}. ${cap.source || "source unknown"}. ${words(cap.kind)}.${
               (cap.departments || []).length ? " " + cap.departments.map(words).join(", ") + "." : ""
             } ${words(cap.certification)}.`}
             onClick={() => onOpen(cap)}
             onKeyDown={(e) => {
               if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(cap); }
             }}>
      <header>
        <span className={"sm-kind sm-kind-" + (KIND_CLASS[cap.kind] || "tool")}>{words(cap.kind)}</span>
        {/* Skills carry departments; tools and MCP entries do not, and the API
            omits the key for them rather than sending []. So this renders
            nothing at all for those cards — no badge, no placeholder, no "none".
            A department is not a thing a tool has. */}
        {(cap.departments || []).map((d) => (
          <span key={d} className="sm-dept" data-slot={deptSlot(d)}>{words(d)}</span>
        ))}
        <span className="sm-dot" data-tone={tone(cap.certification)} aria-hidden="true" />
      </header>
      <h4>{cap.name}</h4>
      {/* The same tool name can be registered from more than one source —
          `compliance_check` is registered three times. Without the source on
          the card, three identical cards are indistinguishable, and an
          assignment cannot say which one it granted. */}
      {cap.source ? <p className="sm-source">{cap.source}</p> : null}
      {cap.description ? <p className="sm-purpose">{cap.description}</p> : null}
      <footer>
        <span className="sm-last">
          LAST USED · <b>{cap.instrumented ? "SEE DETAIL" : "NEVER OBSERVED"}</b>
        </span>
        {cap.callable ? <span className="sm-callable">CALLABLE</span> : null}
      </footer>
    </article>
  );
}

function Detail({ cap, onClose, probe }) {
  // Fetched only when a card is open, so browsing the registry costs one request.
  const usage = useBrainResource(`/brain/capabilities/${encodeURIComponent(cap.capability_id)}/usage`);
  const u = usage.data || {};
  const instrumented = u.instrumented === true;

  return (
    <div className="sm-sheet" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sm-sheet-in" role="dialog" aria-modal="true" aria-label={cap.name}>
        <button className="sm-x" onClick={onClose} aria-label="Close">&times;</button>
        <span className={"sm-kind sm-kind-" + (KIND_CLASS[cap.kind] || "tool")}>{words(cap.kind)}</span>
        {(cap.departments || []).map((d) => (
          <span key={d} className="sm-dept" data-slot={deptSlot(d)}>{words(d)}</span>
        ))}
        <h3>{cap.name}</h3>
        {cap.description ? <p className="sm-body">{cap.description}</p> : null}

        <SectionTitle>Four dimensions, kept apart</SectionTitle>
        <dl className="sm-rows">
          {DIMENSIONS.map((d) => (
            <div className="sm-row" key={d.key}>
              <dt><i className="sm-dot" data-tone={tone(cap[d.key])} />{d.label}</dt>
              <dd data-tone={tone(cap[d.key])}>{words(cap[d.key])}</dd>
            </div>
          ))}
        </dl>

        <SectionTitle>Last used</SectionTitle>
        {usage.status === "loading" ? (
          <div className="skeleton" role="status" aria-label="Loading usage" />
        ) : instrumented ? (
          <dl className="sm-rows">
            <div className="sm-row"><dt>Last used</dt><dd>{u.last_used_at || "—"}</dd></div>
            <div className="sm-row"><dt>Invocations</dt><dd>{u.invocations ?? "—"}</dd></div>
            {/* `skill_retrievals`, not `retrievals`. The projection
                (`capability/projection.py::_summarize`) has only ever emitted
                the former; this read `u.retrievals` from the day it was written
                and therefore rendered an em dash for every capability, however
                many retrievals the trace held. Found once the flag was on and
                the endpoint returned data instead of 404. */}
            <div className="sm-row"><dt>Retrievals</dt><dd>{u.skill_retrievals ?? "—"}</dd></div>
          </dl>
        ) : (
          <div className="sm-never">
            <b>NEVER OBSERVED</b>
            <p>
              The trace spine holds no retrieval or invocation event for this capability,
              so <code>instrumented</code> is <code>false</code> and every counter is{" "}
              <code>null</code>. That is not the same claim as zero uses, and this surface
              will not merge them.
            </p>
          </div>
        )}

        <SectionTitle>Registry</SectionTitle>
        <dl className="sm-rows">
          <div className="sm-row"><dt>Capability id</dt><dd>{cap.capability_id}</dd></div>
          <div className="sm-row"><dt>Source</dt><dd>{cap.source || "—"}</dd></div>
          <div className="sm-row"><dt>Version</dt><dd>{cap.version || "—"}</dd></div>
          <div className="sm-row"><dt>Credential (declared)</dt>
            <dd data-tone={tone(cap.credential_status)}>{words(cap.credential_status)}</dd></div>
          {probe ? (
            <div className="sm-row"><dt>Connection (live)</dt>
              <dd data-tone={tone(probe.outcome)} title={probe.detail || ""}>
                {words(probe.outcome)}
              </dd></div>
          ) : null}
          <div className="sm-row"><dt>Grantable</dt><dd>{cap.grantable ? "YES" : "NO"}</dd></div>
          <div className="sm-row"><dt>Read/write class</dt><dd>{words(cap.read_write_class)}</dd></div>
        </dl>

        <p className="sm-note">
          {cap.kind === "skill"
            ? "A Skill is procedural knowledge and is never grantable — authorization attaches to exact callable Tools only."
            : cap.kind === "mcp_server"
              ? "An MCP server is a container, not a callable. Authority attaches to the individual mcp_tool records it resolves."
              : "Authorization for this capability is a grant naming it exactly, held in capability_grants."}
        </p>
      </div>
    </div>
  );
}

/** The two halves of this surface: what exists, and who may reach it. */
const VIEWS = [
  { id: "library", label: "Library" },
  { id: "access", label: "Skill access" },
];

export default function SkillsMcp() {
  const res = useBrainResource("/brain/capabilities");
  const coverage = useBrainResource("/brain/capabilities/coverage");
  // The LIVE probe, keyed by server id. `credential_status` on the card is a
  // static declaration authored in the capability library and does not move
  // when a credential is connected; this is what the backend can observe now.
  const probes = useBrainResource("/brain/capabilities/mcp/probe");
  const [view, setView] = useState("library");
  const [origin, setOrigin] = useState("all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);

  const all = useMemo(
    () => (res.data && Array.isArray(res.data.capabilities) ? res.data.capabilities : []),
    [res.data]
  );
  const shown = useMemo(
    () => all.filter((c) => matches(c, { origin, query: query.trim().toLowerCase() })),
    [all, origin, query]
  );
  const counts = useMemo(() => {
    const c = { all: all.length, internal: 0, mcp: 0 };
    for (const cap of all) if (c[cap.origin] !== undefined) c[cap.origin] += 1;
    return c;
  }, [all]);

  const cov = coverage.data || {};
  const probeById = useMemo(() => {
    const rows = probes.data && Array.isArray(probes.data.servers)
      ? probes.data.servers : [];
    return Object.fromEntries(rows.map((r) => [r.server_id, r]));
  }, [probes.data]);

  return (
    <>
      <PageHead
        title="Skills & MCP"
        subtitle="Every capability Cipher is registered to use, and what proved it."
      />

      <div className="sm-views" role="tablist" aria-label="Skills and MCP views">
        {VIEWS.map((v) => (
          <button key={v.id} role="tab" aria-selected={view === v.id}
                  className={view === v.id ? "on" : ""}
                  onClick={() => setView(v.id)}>{v.label}</button>
        ))}
      </div>

      {view === "access" ? <SkillAccess /> : res.status === "loading" ? (
        <div className="skeleton" role="status" aria-label="Loading capabilities" />
      ) : res.status === "disabled" ? (
        <EmptyState
          title="Capability API is not enabled in this environment"
          detail="Every route on /api/brain/capabilities returns 404 while CIPHER_CAPABILITY_API_V1 is unset. That is the flag's designed behaviour, not a failure — the registry itself is populated and synced daily."
        />
      ) : res.status === "error" ? (
        <EmptyState
          title="Could not read the capability registry"
          detail="The request failed. Nothing has been changed."
        />
      ) : !all.length ? (
        <EmptyState
          title="No capabilities registered"
          detail="The registry is empty in this environment. capability_sync_daily projects the library into it once every 24 hours."
        />
      ) : (
        <>
          <div className="sm-kpis">
            <div className="sm-kpi">
              <div className="sm-kpi-v">{counts.all}</div>
              <div className="sm-kpi-k">REGISTERED</div>
            </div>
            <div className="sm-kpi">
              <div className="sm-kpi-v">{counts.internal}</div>
              <div className="sm-kpi-k">SKILLS &amp; INTERNAL TOOLS</div>
            </div>
            <div className="sm-kpi">
              <div className="sm-kpi-v">{counts.mcp}</div>
              <div className="sm-kpi-k">MCP</div>
            </div>
            <div className="sm-kpi">
              {/* Reported as numerator and denominator, never a percentage —
                  the endpoint's own doctrine, so "3 of 238" cannot round into
                  a number that sounds like coverage. */}
              <div className="sm-kpi-v" data-tone={cov.numerator ? "value" : "zero"}>
                {cov.numerator ?? "—"}
                <span className="sm-kpi-of"> / {cov.denominator ?? "—"}</span>
              </div>
              <div className="sm-kpi-k">INSTRUMENTED</div>
            </div>
          </div>

          <div className="sm-bar">
            <div className="sm-seg" role="tablist" aria-label="Filter by origin">
              {ORIGINS.map((o) => (
                <button key={o.id} role="tab" aria-selected={origin === o.id}
                        className={origin === o.id ? "on" : ""}
                        onClick={() => setOrigin(o.id)}>
                  {o.label} <b>{counts[o.id] ?? 0}</b>
                </button>
              ))}
            </div>
            <label className="sm-search">
              <input type="search" value={query} aria-label="Search capabilities"
                     placeholder="Search name, purpose or id"
                     onChange={(e) => setQuery(e.target.value)} />
            </label>
          </div>

          <div className="sm-count">
            <b>{shown.length}</b> of {all.length} shown
          </div>

          {shown.length ? (
            <div className="sm-grid">
              {shown.map((cap) => (
                <Card key={cap.capability_id} cap={cap} onOpen={setOpen} />
              ))}
            </div>
          ) : (
            <EmptyState
              title="Nothing matches that filter"
              detail={`The registry holds ${all.length} capabilities; none of them answer this question.`}
            />
          )}

          {open ? (
            <Detail cap={open} onClose={() => setOpen(null)}
                    probe={probeById[open.name] || probeById[open.source_key]} />
          ) : null}
        </>
      )}
    </>
  );
}
